import { useEffect, useMemo, useState } from 'react';
import PermissionMatrix from '../../admin/components/access/PermissionMatrix';
import { listPermissions, listRoles, createRole, updateRole, deleteRole } from '../../admin/api/accessApi';

const emptyForm = {
  roleName: '',
  roleCode: '',
  description: '',
  permissionCodes: [],
  isActive: true,
  roleType: 'CUSTOM',
};

const AdminRoles = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const filteredPermissions = useMemo(() => {
    const query = permissionSearch.trim().toLowerCase();
    if (!query) return permissions;
    return permissions.filter((permission) =>
      [permission.permissionName, permission.permissionCode, permission.moduleName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [permissions, permissionSearch]);

  const roleSections = useMemo(
    () => [
      { label: 'System roles', items: roles.filter((role) => role.isSystemRole) },
      { label: 'Custom roles', items: roles.filter((role) => !role.isSystemRole) },
    ],
    [roles]
  );

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [rolesRes, permissionsRes] = await Promise.all([listRoles(), listPermissions()]);
      setRoles(rolesRes?.data || []);
      setPermissions(
        (permissionsRes?.data || []).map((permission) => ({
          ...permission,
          description:
            permission.description ||
            `Access ${String(permission.permissionName || permission.permissionCode || 'permission').toLowerCase()}.`,
        }))
      );
    } catch (err) {
      setError(err.message || 'Unable to load roles and permissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPermissionSearch('');
  };

  const startRole = (role) => {
    setEditingId(role.id);
    setForm({
      roleName: role.roleName,
      roleCode: role.roleCode,
      description: role.description || '',
      permissionCodes: (role.permissions || []).map((permission) => permission.permissionCode),
      isActive: Boolean(role.isActive),
      roleType: role.roleType,
    });
  };

  const handleTogglePermission = (permissionCode) => {
    if (form.roleType === 'SYSTEM') return;
    setForm((prev) => {
      const set = new Set(prev.permissionCodes);
      if (set.has(permissionCode)) set.delete(permissionCode);
      else set.add(permissionCode);
      return { ...prev, permissionCodes: Array.from(set) };
    });
  };

  const handleSave = async () => {
    setError('');
    if (!form.roleName.trim()) {
      setError('Role name is required.');
      return;
    }
    if (form.roleType === 'SYSTEM') {
      setError('System roles are read-only.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        roleName: form.roleName.trim(),
        roleCode: form.roleCode.trim(),
        description: form.description.trim(),
        permissionCodes: form.permissionCodes,
        isActive: form.isActive,
      };
      if (editingId) {
        await updateRole(editingId, payload);
      } else {
        await createRole(payload);
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || 'Unable to save role.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (role.isSystemRole) return;
    if (!window.confirm(`Delete ${role.roleName}?`)) return;
    try {
      await deleteRole(role.id);
      if (editingId === role.id) {
        resetForm();
      }
      await load();
    } catch (err) {
      setError(err.message || 'Unable to delete role.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.28em] text-[color:var(--admin-icon)]">Access Control</p>
        <h1 className="mt-2 text-2xl font-semibold text-[color:var(--admin-heading)]">Roles & Permissions</h1>
        <p className="mt-2 text-sm text-[color:var(--admin-text)]">
          System roles stay read-only. Custom roles can be created and updated with grouped permissions.
        </p>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
          {error}
        </div>
      )}

      <div className="rounded-[28px] border border-[color:var(--admin-border)] bg-white p-5 shadow-sm">
        <div className="grid items-start gap-5 xl:grid-cols-[minmax(280px,340px)_minmax(0,1fr)]">
          <div className="space-y-4 xl:sticky xl:top-[88px]">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--admin-icon)]">Role name</label>
              <input
                value={form.roleName}
                onChange={(event) => setForm((prev) => ({ ...prev, roleName: event.target.value }))}
                disabled={form.roleType === 'SYSTEM'}
                className="mt-2 w-full rounded-2xl border border-[color:var(--admin-border)] px-4 py-3 text-sm outline-none focus:border-[var(--admin-accent)] disabled:bg-slate-50"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--admin-icon)]">Role code</label>
              <input
                value={form.roleCode}
                onChange={(event) => setForm((prev) => ({ ...prev, roleCode: event.target.value }))}
                disabled={form.roleType === 'SYSTEM'}
                placeholder="Optional. Auto-generated if empty."
                className="mt-2 w-full rounded-2xl border border-[color:var(--admin-border)] px-4 py-3 text-sm outline-none focus:border-[var(--admin-accent)] disabled:bg-slate-50"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--admin-icon)]">Description</label>
              <textarea
                rows="4"
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                disabled={form.roleType === 'SYSTEM'}
                className="mt-2 w-full rounded-2xl border border-[color:var(--admin-border)] px-4 py-3 text-sm outline-none focus:border-[var(--admin-accent)] disabled:bg-slate-50"
              />
            </div>
            <label className="flex items-center gap-3 rounded-2xl border border-[color:var(--admin-border)] bg-[var(--admin-muted-surface)] px-4 py-3 text-sm text-[color:var(--admin-heading)]">
              <input
                type="checkbox"
                checked={form.isActive}
                disabled={form.roleType === 'SYSTEM'}
                onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
              />
              Active status
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={resetForm}
                className="rounded-full border border-[color:var(--admin-border)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[color:var(--admin-heading)]"
              >
                New role
              </button>
              <button
                type="button"
                onClick={load}
                className="rounded-full border border-[color:var(--admin-border)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[color:var(--admin-heading)]"
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="min-w-0">
            <PermissionMatrix
              permissions={filteredPermissions}
              selectedCodes={form.permissionCodes}
              search={permissionSearch}
              onSearchChange={setPermissionSearch}
              onToggle={handleTogglePermission}
              onSelectAll={() =>
                setForm((prev) => ({
                  ...prev,
                  permissionCodes: permissions.map((permission) => permission.permissionCode),
                }))
              }
              onClearAll={() => setForm((prev) => ({ ...prev, permissionCodes: [] }))}
              onSubmit={handleSave}
              submitting={saving}
              roleName={form.roleName}
              editing={Boolean(editingId)}
              roleTypeLabel={form.roleType}
              disabled={form.roleType === 'SYSTEM'}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {roleSections.map((section) => (
          <div key={section.label} className="rounded-[28px] border border-[color:var(--admin-border)] bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-[color:var(--admin-heading)]">{section.label}</h2>
                <p className="mt-1 text-sm text-[color:var(--admin-text)]">
                  {section.label === 'System roles'
                    ? 'System roles are read-only and cannot be deleted.'
                    : 'Custom roles are editable and can be removed after confirmation.'}
                </p>
              </div>
              {loading && section.label === 'System roles' && (
                <div className="text-sm text-[color:var(--admin-text)]">Refreshing...</div>
              )}
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-[color:var(--admin-border)] text-xs uppercase tracking-[0.24em] text-[color:var(--admin-icon)]">
                  <tr>
                    <th className="px-3 py-3 text-left">Role name</th>
                    <th className="px-3 py-3 text-left">Type</th>
                    <th className="px-3 py-3 text-left">Permissions count</th>
                    <th className="px-3 py-3 text-left">Assigned admins</th>
                    <th className="px-3 py-3 text-left">Status</th>
                    <th className="px-3 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {section.items.map((role) => (
                    <tr key={role.id} className="border-b border-[color:var(--admin-border)] last:border-b-0">
                      <td className="px-3 py-4">
                        <div className="font-semibold text-[color:var(--admin-heading)]">{role.roleName}</div>
                        <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[color:var(--admin-icon)]">
                          {role.roleCode}
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${role.isSystemRole ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>
                          {role.roleType}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-[color:var(--admin-text)]">{role.permissionsCount}</td>
                      <td className="px-3 py-4 text-[color:var(--admin-text)]">{role.assignedAdminsCount}</td>
                      <td className="px-3 py-4">
                        <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${role.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>
                          {role.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-4">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => startRole(role)}
                            className="rounded-full border border-[color:var(--admin-border)] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--admin-heading)]"
                          >
                            {role.isSystemRole ? 'View' : 'Edit'}
                          </button>
                          {!role.isSystemRole && (
                            <button
                              type="button"
                              onClick={() => handleDelete(role)}
                              className="rounded-full border border-rose-200 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-rose-600"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!section.items.length && !loading && (
                    <tr>
                      <td colSpan="6" className="px-3 py-8 text-center text-[color:var(--admin-text)]">
                        No roles in this section.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminRoles;
